import React, { useState, useEffect } from "react";
import { Row, Col, Segmented, Skeleton } from "antd";
import { useHistory, useLocation } from "react-router-dom";
import {
  CustomTitle,
  CustomSubTitle,
  CustomButton,
  PhysicianPageWrapper,
} from "../physician_page/styled";
import { PlusOutlined } from "@ant-design/icons";
import AssignJourneyModal from "./modals/AssignJourneyModal";
import CreateJourneyModal from "./modals/CreateJourneyModal";
import CreateTemplateModal from "./modals/CreateTemplateModal";
import ClinicalJourneyCard from "./components/ClinicalJourneyCard";
import CCMPlanCard from "./components/CCMPlanCard";
import TemplateCard from "./components/TemplateCard";
import Analytics from "./components/Analytics";
import styled from "styled-components";
import journeyService from "../../../services/journey";
import ViewTemplatesModal from "./modals/ViewTemplatesModal";
import DeleteConfirmationModal from "../physician_page/component/DeleteConfirmationModal";
import { notify } from "../../../utils/helper";
import { FiUserPlus } from "react-icons/fi";
import { globalRoles } from "../../../utils/roles";
import { extractToken } from "../../../utils/localStorage";
import { CardActions, CardHeader, HeaderContent } from "./components/Styled";

const CardsContainer = styled.div`
  margin-top: 24px;
`;

const JourneyPage = () => {
  const history = useHistory();
  const location = useLocation();
  const user = extractToken();
  const [isAssignJourneyModalOpen, setIsAssignJourneyModalOpen] =
    useState(false);
  const [isCreateJourneyModalOpen, setIsCreateJourneyModalOpen] =
    useState(false);
  const [isAssistantModalOpen, setIsAssistantModalOpen] = useState(false);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [templatesData, setTemplatesData] = useState([]);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [templateData, setTemplateData] = useState([]);
  const [isTemplateEdit, setIsTemplateEdit] = useState(false);
  const [templateId, setTemplateId] = useState(null);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [selectedClinicalId, setSelectedClinicalId] = useState("");
  const [templateForJourney, setTemplateForJourney] = useState(null);
  const [isCreateTemplateModalOpen, setIsCreateTemplateModalOpen] =
    useState(false);
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(9);
  const [hasMore, setHasMore] = useState(true);
  const [journeyName, setJourneyName] = useState("");
  const [journeyData, setJourneyData] = useState([]);
  const isPhysician = user?.role === globalRoles.PHYSICIAN;
  const isScheduler = user?.role === globalRoles.ASSISTANT;
  const isCcmCoordinator = user?.role === globalRoles.CCM_COORDINATOR;
  const isSuperAdmin = user?.role === globalRoles.SUPERADMIN;
  const isCcmManager = user?.role === globalRoles.CCM_MANAGER;
  const isLibraryManager = user?.role === globalRoles.LIBRARY_MANAGER;


  // Map segment values to URL-friendly values
  const segmentToUrlMap = {
    "Clinical Journeys": "clinical-journeys",
    "CCM Care Plans": "ccm-care-plans",
    Templates: "templates",
    Analytics: "analytics",
  };

  // Reverse map for URL to segment
  const urlToSegmentMap = Object.fromEntries(
    Object.entries(segmentToUrlMap).map(([key, value]) => [value, key])
  );

  const fetchJourneys = async (value, page = 1, isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadMoreLoading(true);
      } else {
        setLoading(true);
      }
      const response = await journeyService.getJourneys(value, pageSize, page);
      const journeysData = response?.data?.journeys?.results || [];
      
      if (isLoadMore) {
        setJourneys((prev) => [...prev, ...journeysData]);
      } else {
        setJourneys(journeysData);
      }
      
      setHasMore(journeysData.length === pageSize);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching journeys:", error);
      if (!isLoadMore) setJourneys([]);
    } finally {
      setLoading(false);
      setLoadMoreLoading(false);
    }
  };

  // Get initial journey type from URL or default
  const getJourneyTypeFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab && urlToSegmentMap[tab]) {
      return urlToSegmentMap[tab];
    }
    return isSuperAdmin || isLibraryManager || isPhysician ? "Clinical Journeys" : isSuperAdmin || isCcmCoordinator ? "CCM Care Plans" : "Templates";
  };

  const [journeyType, setJourneyType] = useState(getJourneyTypeFromUrl());

  // Update URL when journey type changes
  useEffect(() => {
    const urlTab = segmentToUrlMap[journeyType];
    const params = new URLSearchParams(location.search);
    params.set("tab", urlTab);
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyType]);

  // Update journey type when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const newJourneyType = getJourneyTypeFromUrl();
    if (newJourneyType !== journeyType) {
      setJourneyType(newJourneyType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleLogTime = (planId) => {
    // TODO: Implement log time functionality
  };

  const handleUseTemplate = (template) => {
    // Find the template from templatesData if only ID is passed
    const templateData =
      typeof template === "object"
        ? template
        : templatesData.find((t) => t.id === template || t._id === template);

    if (templateData) {
      setTemplateForJourney(templateData);
      setIsCreateJourneyModalOpen(true);
    }
  };

  const handleTemplatePreview = (templateData) => {
    setTemplateData(templateData);
    setIsPreviewModalOpen(true);
  };

  const handleTemplateEdit = (templateData) => {
    setTemplateData(templateData);
    setIsTemplateEdit(true);
    setIsPreviewModalOpen(true);
  };

  const handleTemplateDelete = (id) => {
    setTemplateId(id);
    setIsDeleteModal(true);
  };

    // if (loading) {
    //   return (
    //     <Row gutter={[16, 16]} style={{ width: "100%" }}>
    //       {[1, 2, 3].map((idx) => (
    //         <Col span={8} key={idx}>
    //           <PhysicianPageWrapper>
    //             <CardHeader>
    //               <HeaderContent>
    //                 <Skeleton.Button
    //                   active
    //                   size="small"
    //                   style={{ width: 80, height: 24 }}
    //                 />
    //               </HeaderContent>
    //               <Skeleton.Button
    //                 active
    //                 size="small"
    //                 style={{ width: 24, height: 24 }}
    //               />
    //             </CardHeader>
    //             <Skeleton
    //               active
    //               paragraph={{ rows: 2 }}
    //               style={{ marginBottom: 16 }}
    //             />
  
    //             <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
    //               {[1, 2, 3, 4].map((i) => (
    //                 <Col xs={12} sm={12} lg={6} key={i}>
    //                   <Skeleton active paragraph={{ rows: 2 }} />
    //                 </Col>
    //               ))}
    //             </Row>
    //             <Skeleton
    //               active
    //               paragraph={{ rows: 1 }}
    //               style={{ marginBottom: 16 }}
    //             />
    //             <CardActions>
    //               <Skeleton.Button
    //                 active
    //                 size="default"
    //                 style={{ flex: 1, height: 36 }}
    //               />
    //               <Skeleton.Button
    //                 active
    //                 size="default"
    //                 style={{ flex: 1, height: 36 }}
    //               />
    //             </CardActions>
    //           </PhysicianPageWrapper>
    //         </Col>
    //       ))}
    //     </Row>
    //   );
    // }
  

  return (
    <div>
      {isAssignJourneyModalOpen && (
        <AssignJourneyModal
          isOpen={isAssignJourneyModalOpen}
          setOpen={setIsAssignJourneyModalOpen}
          journeyId={selectedClinicalId}
          selectedClinicalId={selectedClinicalId}
          setSelectedClinicalId={setSelectedClinicalId}
          onSuccess={() => {
            fetchJourneys(journeyName);
          }}
          journeyData={journeyData}
        />
      )}
      {isCreateJourneyModalOpen && (
        <CreateJourneyModal
          isOpen={isCreateJourneyModalOpen}
          setOpen={(open) => {
            setIsCreateJourneyModalOpen(open);
            if (!open) {
              setTemplateForJourney(null);
            }
          }}
          templateData={templateForJourney}
          onSuccess={() => {
            setTemplateForJourney(null);
            setIsCreateJourneyModalOpen(false);
            fetchJourneys(journeyName);
            // Refresh journeys if needed
            if (
              journeyType === "Clinical Journeys" ||
              journeyType === "CCM Care Plans"
            ) {
              // The card components will handle their own refresh
            }
          }}
        />
      )}

      

      <PhysicianPageWrapper style={{ padding: "40px 60px" }}>
        <Row className="custom-header">
          <Col className="header-item">
            <CustomTitle>Patient Journey Management</CustomTitle>

            <CustomSubTitle>
              Manage clinical journeys and CCM care plans
            </CustomSubTitle>
          </Col>
          <Col className="custom-btn">
            {/* {selectedClinicalId.length > 0 && (
              <CustomButton
                onClick={() => {
                  setIsAssignJourneyModalOpen(true);
                }}
              >
                <FiUserPlus className="font-18" />
                Assign Journey
              </CustomButton>
            )} */}
            {journeyType !== "Templates" && (
              <CustomButton
                onClick={() => {
                  setIsCreateJourneyModalOpen(true);
                }}
              >
                <PlusOutlined className="font-18 mt-5" />
                Create New Journey
              </CustomButton>
            )}
            {journeyType === "Templates" && (
              <CustomButton
                onClick={() => {
                  setIsCreateTemplateModalOpen(true);
                }}
              >
                <PlusOutlined className="font-18 mt-5" />
                Create New Template
              </CustomButton>
            )}
          </Col>
        </Row>

        <Row style={{ padding: "25px 0" }}>
          <Segmented
            className="w-100"
            style={{ backgroundColor: "#f1f5f9" }}
            options={[
              (isSuperAdmin || isLibraryManager || isPhysician || isScheduler) && "Clinical Journeys",
              (isSuperAdmin || isLibraryManager || isCcmCoordinator || isCcmManager) && "CCM Care Plans",
              "Templates",
              "Analytics",
            ].filter(Boolean)}
            block
            value={journeyType}
            onChange={(value) => {
              setJourneyType(value);
              setSelectedClinicalId("");
              setJourneys([]);
              setCurrentPage(1);
              setHasMore(true);
            }}
          />
        </Row>

        {journeyType === "Clinical Journeys" && (isSuperAdmin || isLibraryManager || isPhysician || isScheduler) && (
          <CardsContainer>
            <ClinicalJourneyCard
              selectedClinicalId={selectedClinicalId}
              setSelectedClinicalId={setSelectedClinicalId}
              fetchJourneys={fetchJourneys}
              journeys={journeys}
              loading={loading}
              setJourneyName={setJourneyName}
              journeyData={journeyData}
              setJourneyData={setJourneyData}
              currentPage={currentPage}
              hasMore={hasMore}
              loadMoreLoading={loadMoreLoading}
              setIsAssignJourneyModalOpen={setIsAssignJourneyModalOpen}
            />
          </CardsContainer>
        )}

        {journeyType === "CCM Care Plans" && (isSuperAdmin || isLibraryManager || isCcmCoordinator || isCcmManager) && (
          <CardsContainer>
            <CCMPlanCard
              selectedClinicalId={selectedClinicalId}
              setSelectedClinicalId={setSelectedClinicalId}
              fetchJourneys={fetchJourneys}
              journeys={journeys}
              loading={loading}
              setJourneyName={setJourneyName}
              journeyData={journeyData}
              setJourneyData={setJourneyData}
              currentPage={currentPage}
              hasMore={hasMore}
              loadMoreLoading={loadMoreLoading}
              setIsAssignJourneyModalOpen={setIsAssignJourneyModalOpen}
            />
          </CardsContainer>
        )}

        {journeyType === "Templates" && (
          <CardsContainer>
            <TemplateCard isCreateTemplateModalOpen={isCreateTemplateModalOpen} setIsCreateTemplateModalOpen={setIsCreateTemplateModalOpen} />
          </CardsContainer>
        )}

        {journeyType === "Analytics" && <Analytics />}
      </PhysicianPageWrapper>
    </div>
  );
};

export default JourneyPage;
